import { useAppSelector } from 'hooks/useAppSelector';
import { MouseEvent, useCallback } from 'react';
import { Accordion } from 'react-bootstrap';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { lifecycleSelectedSectionChanged } from 'state/discover/page/page.actions';
import type { LifeCycleCategory } from 'state/discover/page/page.model';
import { getSelectedLifecycleSectionCategory } from 'state/discover/page/page.selector';
import { secondaryMarketQuoteByIsinReloadRequested } from 'state/secondaryMarket/realtimeQuote/quote';
import type { AppState } from 'state/state.model';
import { getLifecyclePortalUrl } from 'state/user';

import { RequestAccess } from '../../RequestAccess';
import { NearToAutocallSection } from './near-to-autocall-section';
import { NearToCapitalBarrierSection } from './near-to-capital-barrier-section';
import { NearToMaturitySection } from './near-to-maturity-section';
import { WithCapitalBarrierEventSection } from './with-capital-barrier-event-section';
import { WithNoMoreOptionValueItem } from './with-no-more-option-value-item';

export function LifecycleRequestAccess() {
  const lifecyclePortalUrl = useSelector((state: AppState) => getLifecyclePortalUrl(state));

  return (
    <RequestAccess e2e="life-cycle-item-request-access" url={lifecyclePortalUrl} labelKey="discover.lifeCycle.portal" />
  );
}

export function LifeCycleList() {
  const activeCategory = useAppSelector(getSelectedLifecycleSectionCategory);
  const dispatch = useDispatch();

  const onCategoryItemClicked = useCallback(
    (event: MouseEvent) => {
      const newCategory = event.currentTarget.id;
      if (newCategory !== activeCategory) {
        logAnalytics.trackEvent('Discover', 'Expand Lifecycle category');
        dispatch(lifecycleSelectedSectionChanged(newCategory as LifeCycleCategory));
      }
    },
    [activeCategory, dispatch],
  );
  const openInSecondary = useCallback(
    (isin: any) => dispatch(secondaryMarketQuoteByIsinReloadRequested(isin, 'secondaryMarketTrades')),
    [dispatch],
  );

  return (
    <div className="p-3" data-e2e="life-cycle">
      <ErrorBoundary FallbackComponent={LifecycleRequestAccess}>
        <Accordion className="list-group list-group-flush" as={'ul'} activeKey={activeCategory}>
          <NearToMaturitySection
            id="near-to-maturity"
            isActive={activeCategory === 'near-to-maturity'}
            category="near-to-maturity"
            onClick={onCategoryItemClicked}
            openInSecondary={openInSecondary}
          />
          <NearToAutocallSection
            id="near-to-autocall"
            isActive={activeCategory === 'near-to-autocall'}
            category="near-to-autocall"
            onClick={onCategoryItemClicked}
            openInSecondary={openInSecondary}
          />
          <WithCapitalBarrierEventSection
            id="with-capital-barrier-event"
            isActive={activeCategory === 'with-capital-barrier-event'}
            category="with-capital-barrier-event"
            onClick={onCategoryItemClicked}
            openInSecondary={openInSecondary}
          />
          <NearToCapitalBarrierSection
            id="near-to-capital-barrier"
            isActive={activeCategory === 'near-to-capital-barrier'}
            category="near-to-capital-barrier"
            onClick={onCategoryItemClicked}
            openInSecondary={openInSecondary}
          />
          <WithNoMoreOptionValueItem
            id="with-no-more-option-value"
            isActive={activeCategory === 'with-no-more-option-value'}
            category="with-no-more-option-value"
            onClick={onCategoryItemClicked}
            openInSecondary={openInSecondary}
          />
        </Accordion>
      </ErrorBoundary>
    </div>
  );
}
